import { DEPLOYMENT_TAG_KEY, DEPLOY_EXEC_ROLE_NAME } from './deploy-policy.js';
import { LOCK_TABLE_NAME, STATE_BUCKET_PREFIX } from './template.js';
/**
 * An adopted instance's state where it already is, for a scoped deploy role
 * (IM-214).
 *
 * ## The gap
 *
 * A scoped deploy role reads Terraform state by bucket *name*:
 * `qrvey-tfstate-<account>-*` under the run's own `<id>/`, or
 * `qrveyversion-<id>`. An instance created by hand and then adopted keeps its
 * state wherever its builder put it — `qrveyversion-us-west-2-amit` held
 * `es9kp25n`'s — so the runner's state-bucket check refused every run. Tags do
 * not help: no statement reads one, and S3 grants by name.
 *
 * ## The answer: a grant the customer deploys, pinned to one instance
 *
 * One inline policy on the deploy role, `qrvey-adopted-state-<id>`, allowing
 * that bucket, that one object and its two lock items — and only when the
 * session's `qrvey-id` tag is this instance's id. So a run for any other
 * deployment gains nothing, which is the property the scoped role exists to
 * keep. Four things about it:
 *
 * - **Inline, not managed.** The scoped role already carries nine managed
 *   policies against IAM's default quota of ten; inline policies count only
 *   toward 10,240 characters per role, which leaves room for about ten of
 *   these.
 * - **The deploy role cannot undo or widen it.** A scoped run may not put,
 *   delete or attach a policy on any role outside the installer's names
 *   (`DenyRolesOutsideInstallerNames`), and its own role is not one of them.
 * - **The customer owns it.** Qrvey never writes it, as with the boundary
 *   (IM-192); deleting the stack takes the access away.
 * - **Nothing here can be a wildcard.** A bucket or key carrying `*` or `?`
 *   would grant more than one object, and `${` would be read as a policy
 *   variable or a CloudFormation substitution — so `adoptedStateProblem`
 *   refuses all three before anything is rendered.
 *
 * Partition-agnostic, like every template here: `${AWS::Partition}` and
 * `${AWS::AccountId}` come from where the stack is deployed. The lock table is
 * named in the instance's own region, because IAM is global and the stack may
 * be deployed anywhere.
 */
/** The inline policy's name, one per instance. */
export const adoptedStatePolicyName = (deploymentId) => `qrvey-adopted-state-${deploymentId}`;
/** An S3 bucket name as S3 allows it, and so with no wildcard in it. */
const BUCKET_PATTERN = /^[a-z0-9][a-z0-9.-]{1,61}[a-z0-9]$/;
/**
 * Why a recorded state location cannot be granted, or `undefined` when it can.
 * Every case is a grant that would cover more, or other, than one object.
 */
export const adoptedStateProblem = (bucket, key) => {
    if (!BUCKET_PATTERN.test(bucket)) {
        return `The state bucket "${bucket}" is not an S3 bucket name, so no grant can name it exactly.`;
    }
    if (key === '' || key.length > 1024)
        return 'The state key must be 1 to 1024 characters.';
    if (/[*?]/.test(key)) {
        return 'The state key contains * or ?, which IAM reads as a wildcard, so a grant for it would cover other objects too.';
    }
    if (key.includes('${')) {
        return 'The state key contains "${", which IAM and CloudFormation would both read as a variable.';
    }
    // eslint-disable-next-line no-control-regex
    if (/[\u0000-\u001f\u007f]/.test(key))
        return 'The state key contains a control character.';
    return undefined;
};
/**
 * Whether a scoped deploy role reaches this state without a grant: the shapes
 * its own `StateBucket`, `OwnStateObjects` and `OwnBuckets` statements allow.
 * The account in the bucket name is not checked here; the runner checks
 * ownership itself, before it reads anything.
 */
export const stateReachableWhenScoped = (deploymentId, bucket, key) => (bucket.startsWith(`${STATE_BUCKET_PREFIX}-`) && key.startsWith(`${deploymentId}/`)) ||
    bucket === `qrveyversion-${deploymentId}`;
/** The one-resource template the customer deploys. Throws for a location `adoptedStateProblem` refuses. */
export const buildAdoptedStateTemplate = (request) => {
    const problem = adoptedStateProblem(request.bucket, request.key);
    if (problem !== undefined)
        throw new Error(problem);
    const { deploymentId, region, bucket, key } = request;
    const onlyThisDeployment = {
        StringEquals: { [`aws:PrincipalTag/${DEPLOYMENT_TAG_KEY}`]: deploymentId },
    };
    const sub = (arn) => ({ 'Fn::Sub': arn });
    // CloudFormation refuses a description over 1,024 characters, and a key may
    // be that long alone; the policy names the object either way.
    const where = `s3://${bucket}/${key}`;
    const description = `Lets Qrvey runs for deployment ${deploymentId}, and no other, use its Terraform state where it already is${where.length <= 600 ? `: ${where}` : ''}. Yours to own: Qrvey never writes it, and deleting this stack takes the access away.`;
    return {
        AWSTemplateFormatVersion: '2010-09-09',
        Description: description,
        Resources: {
            AdoptedStateAccess: {
                Type: 'AWS::IAM::Policy',
                Properties: {
                    PolicyName: adoptedStatePolicyName(deploymentId),
                    Roles: [DEPLOY_EXEC_ROLE_NAME],
                    PolicyDocument: {
                        Version: '2012-10-17',
                        Statement: [
                            {
                                // What the runner's ownership check and Terraform's backend ask of the bucket.
                                Sid: 'AdoptedStateBucket',
                                Effect: 'Allow',
                                Action: ['s3:ListBucket', 's3:GetBucketLocation', 's3:GetBucketVersioning'],
                                Resource: sub(`arn:\${AWS::Partition}:s3:::${bucket}`),
                                Condition: onlyThisDeployment,
                            },
                            {
                                // The one state file, and nothing else in the bucket.
                                Sid: 'AdoptedStateObject',
                                Effect: 'Allow',
                                Action: ['s3:GetObject', 's3:PutObject'],
                                Resource: sub(`arn:\${AWS::Partition}:s3:::${bucket}/${key}`),
                                Condition: onlyThisDeployment,
                            },
                            {
                                // Terraform's lock and digest items for that file, keyed `<bucket>/<key>`.
                                Sid: 'AdoptedStateLock',
                                Effect: 'Allow',
                                Action: ['dynamodb:GetItem', 'dynamodb:PutItem', 'dynamodb:DeleteItem'],
                                Resource: sub(`arn:\${AWS::Partition}:dynamodb:${region}:\${AWS::AccountId}:table/${LOCK_TABLE_NAME}`),
                                Condition: {
                                    ...onlyThisDeployment,
                                    'ForAllValues:StringEquals': {
                                        'dynamodb:LeadingKeys': [`${bucket}/${key}`, `${bucket}/${key}-md5`],
                                    },
                                    Null: { 'dynamodb:LeadingKeys': 'false' },
                                },
                            },
                        ],
                    },
                },
            },
        },
    };
};
//# sourceMappingURL=adopted-state.js.map